import * as PIXI from "pixi.js"
import { isFiniteNumber } from "../../../utilities/math-utils"
import { CaseDataWithSubPlot } from "../d3-types"
import { PointDisplayType, transitionDuration } from "../data-display-types"
import { PixiTransition, TransitionPropMap, TransitionProp } from "../pixi/pixi-transition"
import { PointerState } from "../models/pointer-state"
import {
  circleAnchor,
  getRendererForEvent,
  hBarAnchor,
  PointRendererBase
} from "./point-renderer-base"
import { PointsState } from "./points-state"
import { coalesceBars, IBarPiece, pointStateToBarPiece } from "./bar-coalescing"
import { kDefaultPointShape, PointShape } from "../../../utilities/point-shape-utils"
import {
  isPointInShapeGeometry, PointShapeGeometry, pointShapeBoundingRadius, pointShapeGeometry,
  pointShapeSymmetricExtent
} from "./point-shapes"
import {
  IBackgroundEventDistributionOptions,
  IPoint,
  IPointMetadata,
  IPointRendererOptions,
  IPointStyle,
  RendererCapability
} from "./point-renderer-types"

/*
 * The region of the shape's own coordinates that becomes the texture.
 *
 * A sprite draws its texture around its anchor, which for points is the middle of the texture, so
 * the texture has to be centered on the point rather than on the ink. Left to size itself it takes
 * the bounds of the ink, and a triangle's ink sits high -- centering that would draw the triangle
 * low by about a third of its radius.
 *
 * Padded by the stroke, which straddles the outline and would otherwise clip at the widest vertices.
 *
 * Rounded up to whole units, because generateTexture truncates the frame's width and height to
 * integers but translates by the origin it was given: a frame 15.6 wide becomes a 15-wide texture
 * holding the shape's center at 7.8, while the anchor puts the texture's own center at 7.5, so the
 * shape draws 0.3px right of the position it is hit tested at. How far off depends on the shape, so
 * a point given a new shape lands somewhere slightly different than it sat before. Rounding up
 * rather than to nearest keeps the full stroke padding.
 */
function symmetricFrame(shape: PointShape, radius: number, strokeWidth: number): PIXI.Rectangle {
  const { w, h } = pointShapeSymmetricExtent(shape, radius)
  const paddedW = Math.ceil(w + 2 * strokeWidth)
  const paddedH = Math.ceil(h + 2 * strokeWidth)
  return new PIXI.Rectangle(-paddedW / 2, -paddedH / 2, paddedW, paddedH)
}

/*
 * Hit tests a sprite against the shape drawn on it rather than the rectangle of its texture, which
 * is what a sprite falls back to and is looser than even the circle itself.
 *
 * PIXI hands `contains` the pointer in the sprite's own coordinates, where the origin is the point's
 * position, so these are the same offsets the canvas hit tester works in and both renderers agree
 * on what counts as a hit.
 *
 * Everything this needs is held rather than derived, because PIXI's hitPruneFn calls `contains` on
 * every interactive sprite with no cheaper bounds test of its own, and the sprites are all
 * interactive: one pointer move over a dense plot is one call per point. Building a star's outline
 * on each of them costs about 0.9ms across 5,000 points, nearly all of it spent answering misses.
 */
class PointShapeHitArea {
  private geometry: PointShapeGeometry
  // beyond this nothing can be on the point, which is the answer for nearly every call
  private reachSq: number

  constructor(public shape: PointShape, public radius: number) {
    this.geometry = pointShapeGeometry(shape, radius)
    this.reachSq = PointShapeHitArea.reachSqFor(shape, radius)
  }

  update(shape: PointShape, radius: number): void {
    if (shape === this.shape && radius === this.radius) return

    this.shape = shape
    this.radius = radius
    this.geometry = pointShapeGeometry(shape, radius)
    this.reachSq = PointShapeHitArea.reachSqFor(shape, radius)
  }

  contains(x: number, y: number): boolean {
    if (x * x + y * y > this.reachSq) return false
    return isPointInShapeGeometry(this.geometry, this.radius, x, y)
  }

  // the ink can stop short of r -- a plus does, in its notches -- and the containment test answers
  // for the circle there, so the reach is whichever of the two goes further
  private static reachSqFor(shape: PointShape, radius: number): number {
    const reach = Math.max(radius, pointShapeBoundingRadius(shape, radius))
    return reach * reach
  }
}

const DEFAULT_Z_INDEX = 0
const RAISED_Z_INDEX = 100
const MAX_SPRITE_SCALE = 2

const strokeColor = "#ffffff"
const strokeColorHover = "#a35b3a"

// without this cast using TypeScript 5.7, we get the following error:
// "Type 'FederatedPointerEvent' is missing the following properties
// from type 'PointerEvent': altitudeAngle, azimuthAngle"
const toPointerEvent = (event: PIXI.FederatedPointerEvent) => event as unknown as PointerEvent
const toFederatedPointerEvent = (event: PointerEvent) => event as unknown as PIXI.FederatedPointerEvent

const hoverRadiusFactor = 1.5

interface IDisplayTypeTransitionState {
  isActive: boolean
}

interface IPointTransitionState {
  hasTransitioned: boolean
}

export const PixiPointsAnimationFrameRequestIds = ["deselectAll"] as const
export type PixiPointsAnimationFrameRequestId = typeof PixiPointsAnimationFrameRequestIds[number]

/**
 * WebGL point renderer using PIXI.js.
 * This is an adapter that extends PointRendererBase and uses PIXI.js for actual rendering.
 */
export class PixiPointRenderer extends PointRendererBase {
  // PIXI.js components
  private renderer?: PIXI.Renderer
  private stage = new PIXI.Container()
  private pointsContainer = new PIXI.Container()
  private background = new PIXI.Sprite(PIXI.Texture.EMPTY)
  private subPlotMasks: PIXI.Graphics[] = []
  private ticker = new PIXI.Ticker()
  private tickerStopTimeoutId: number | undefined

  // Safari workarounds
  private isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  private mostRecentSvgElement: SVGElement | null = null

  // Sprite management
  private sprites = new Map<string, PIXI.Sprite>()
  private textures = new Map<string, PIXI.Texture>()

  // Bars are drawn by coalescing contiguous same-style cases into one solid segment rect each
  // (see updateBarsLayer / bar-coalescing) rather than one sprite per case. There is one Graphics
  // per subplot cell (created in doResize, masked to its cell) so split-plot cells coalesce and
  // clip independently. The per-case sprites are hidden (renderable = false) while this layer is
  // active; they remain in the scene only because points mode and the points<->bars fuse
  // transition still use them. Bar clicks are handled separately by the SVG bar covers (see
  // renderBarCovers), not these sprites.
  private barsGraphicsBySubplot: PIXI.Graphics[] = []
  private barsLayerActive = false

  // Transition state
  private displayTypeTransitionState: IDisplayTypeTransitionState = { isActive: false }
  private pointTransitionStates = new Map<string, IPointTransitionState>()
  private currentTransition?: PixiTransition
  private targetProp: TransitionPropMap = {}
  private startProp: TransitionPropMap = {}

  // Resize observer
  private resizeObserver?: ResizeObserver


  // Callback for when the browser forcibly reclaims this renderer's WebGL context
  private _onBrowserContextLoss?: () => void
  private boundContextLostHandler?: (e: Event) => void

  constructor(state?: PointsState) {
    super(state)
  }

  /**
   * Set a callback to be invoked if the browser forcibly reclaims this renderer's
   * WebGL context (e.g., because too many contexts are active).
   */
  set onBrowserContextLoss(callback: (() => void) | undefined) {
    this._onBrowserContextLoss = callback
  }

  // ===== Abstract property implementations =====

  get canvas(): HTMLCanvasElement | null {
    return this.renderer?.view.canvas as HTMLCanvasElement ?? null
  }

  get capability(): RendererCapability {
    return "webgl"
  }

  // The live WebGL canvas reads as empty from external pipelines (e.g. html-to-image),
  // so extract a static snapshot via PIXI's extract API instead. The extract call can
  // throw on context loss or partial init, so callers see a null snapshot on failure
  // rather than a thrown error.
  override snapshotCanvas(): HTMLCanvasElement | null {
    if (!this.renderer) return null
    try {
      return this.renderer.extract.canvas(this.stage) as HTMLCanvasElement
    } catch (e) {
      console.warn("[PixiPointRenderer] snapshotCanvas extract failed:", e)
      return null
    }
  }

  get anyTransitionActive(): boolean {
    return PixiTransition.anyTransitionActive(this.targetProp)
  }

  /**
   * Get the textures map (for testing/debugging purposes).
   * Keys are JSON-stringified style objects, values are PIXI.Texture instances.
   */
  get texturesMap(): Map<string, PIXI.Texture> {
    return this.textures
  }

  // ===== Abstract method implementations =====

  protected async doInit(options?: IPointRendererOptions): Promise<void> {
    try {
      this.renderer = await PIXI.autoDetectRenderer({
        resolution: window.devicePixelRatio,
        autoDensity: true,
        backgroundAlpha: 0,
        antialias: true,
        eventMode: "passive",
        // Allow software rendering fallback for CI/headless environments where hardware
        // acceleration may not be available. Without this, renderer initialization can
        // silently fail in GitHub Actions or other headless Chrome environments.
        failIfMajorPerformanceCaveat: false,
        eventFeatures: {
          move: true,
          click: true,
          globalMove: false,
          wheel: false
        }
      })
    } catch (e) {
      console.warn("[PixiPointRenderer] PIXI.autoDetectRenderer FAILED", e)
      throw new Error(`PixiPointRenderer initialization failed: ${e instanceof Error ? e.message : String(e)}`)
    }

    if (!this.renderer) {
      const msg = "renderer is null after initialization - WebGL may not be supported"
      console.warn(`[PixiPointRenderer] ${msg}`)
      throw new Error(`PixiPointRenderer initialization failed: ${msg}`)
    }

    // Check if this renderer's GL context was killed during creation. When multiple
    // renderers init in the same batch, later inits can cause the browser to kill older
    // contexts. Since all inits happen synchronously (async only for module loading),
    // the webglcontextlost events don't fire until after the batch completes.
    // We check here so we can fail fast and fall back to canvas.
    const gl = "gl" in this.renderer ? this.renderer.gl : undefined
    if (gl?.isContextLost()) {
      // GL context exists but was lost — report to the manager so it can learn the limit
      this._onBrowserContextLoss?.()
      this.renderer.destroy()
      this.renderer = undefined
      throw new Error("PixiPointRenderer initialization failed: WebGL context lost during creation")
    } else if (!gl) {
      // No GL context (e.g., non-WebGL renderer) — fail without reporting context loss
      this.renderer.destroy()
      this.renderer = undefined
      throw new Error("PixiPointRenderer initialization failed: no WebGL context available")
    }

    // Listen for browser-initiated context loss that occurs AFTER successful init
    // (e.g., if another component later allocates too many contexts).
    const canvas = this.renderer.view.canvas as HTMLCanvasElement
    this.boundContextLostHandler = () => {
      if (!this.isDisposed) {
        this._onBrowserContextLoss?.()
      }
    }
    canvas.addEventListener("webglcontextlost", this.boundContextLostHandler)

    this.ticker.add(this.tick.bind(this))
    this.stage.addChild(this.background)
    this.stage.addChild(this.pointsContainer)
    this.pointsContainer.sortableChildren = true
    // The coalesced-bars layers (one per subplot cell, masked to that cell) are created in
    // doResize once the subplot masks exist.

    if (options?.backgroundEventDistribution) {
      this.doSetupBackgroundEventDistribution(options.backgroundEventDistribution)
    }

    if (options?.resizeTo) {
      this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect
          this.doResize(width, height, 1, 1, 1, 1)
        }
      })
      this.resizeObserver.observe(options.resizeTo)
    }

    // Sync any existing state to create sprites
    this.syncFromState()

    // Start rendering if we have sprites
    if (this.sprites.size > 0) {
      this.doStartRendering()
    }
  }

  protected doDispose(): void {
    // Remove context loss listener before destroying the renderer
    if (this.boundContextLostHandler) {
      const canvas = this.renderer?.view.canvas as HTMLCanvasElement | undefined
      canvas?.removeEventListener("webglcontextlost", this.boundContextLostHandler)
      this.boundContextLostHandler = undefined
    }
    this.ticker.stop()
    this.ticker.destroy()
    this.renderer?.destroy()
    this.renderer = undefined
    // Destroy the per-subplot bar layers (clearing their mask refs) before the masks they use,
    // then the masks, then the stage.
    this.barsGraphicsBySubplot.forEach(graphics => {
      graphics.mask = null
      graphics.destroy()
    })
    this.barsGraphicsBySubplot = []
    this.subPlotMasks.forEach(mask => mask.destroy())
    this.subPlotMasks = []
    this.stage.destroy()
    this.textures.forEach(texture => texture.destroy())
    this.textures.clear()
    this.resizeObserver?.disconnect()
    this.sprites.clear()
  }

  protected doResize(
    width: number,
    height: number,
    xCats: number,
    yCats: number,
    topCats: number,
    rightCats: number
  ): void {
    if (width > 0 && height > 0) {
      this.renderer?.resize(width, height)
      this.background.width = width
      this.background.height = height
      this.doStartRendering()
    }

    const maskWidth = width / (xCats * topCats)
    const maskHeight = height / (yCats * rightCats)

    // Clear sprite mask references before destroying old masks
    this.sprites.forEach(sprite => {
      sprite.mask = null
    })
    // Defer destruction of old masks (see deferDestroy)
    const oldMasks = this.subPlotMasks
    this.subPlotMasks = []
    this.deferDestroy(oldMasks)

    // Create new masks
    for (let top = 0; top < topCats; ++top) {
      for (let right = rightCats - 1; right >= 0; --right) {
        for (let left = yCats - 1; left >= 0; --left) {
          for (let bottom = 0; bottom < xCats; ++bottom) {
            const r = right * yCats + left
            const c = top * xCats + bottom
            const x = c * maskWidth
            const y = r * maskHeight
            const mask = new PIXI.Graphics()
              .rect(x, y, maskWidth, maskHeight)
              .fill(0xffffff)
            // Set explicit bounds for the mask (required in PIXI v8 for masks not in display list)
            mask.boundsArea = new PIXI.Rectangle(x, y, maskWidth, maskHeight)
            this.subPlotMasks.push(mask)
          }
        }
      }
    }

    // Re-apply masks to existing sprites based on their subPlotNum in state
    this.reapplyMasksFromState()
    // Rebuild the per-subplot coalesced-bars layers to match the new masks
    this.rebuildBarsGraphics()
    // Trigger a render after masks are reapplied
    this.doStartRendering()
  }

  // (Re)creates one coalesced-bars Graphics per subplot cell, each masked to that cell, so split
  // plots coalesce and clip per cell. eventMode "none" keeps the layer from swallowing pointer
  // events (empty-space clicks should reach the background to deselect; bar clicks are handled by
  // the SVG bar covers). pointsContainer has no transform, so the stage shares its coordinate space.
  // Destroy PIXI objects on the next animation frame rather than synchronously: an in-flight render
  // this frame may still reference them, and rendering a destroyed object throws (null GPU context).
  // Used when swapping out subplot masks and the per-subplot bar layers. See CODAP-1234.
  private deferDestroy(objects: Array<{ destroy(): void }>): void {
    requestAnimationFrame(() => objects.forEach(obj => obj.destroy()))
  }

  private rebuildBarsGraphics(): void {
    // Remove the old layers from the stage and clear their masks now (so they are no longer
    // collected for rendering), but defer destroy() to the next frame (see deferDestroy).
    const oldGraphics = this.barsGraphicsBySubplot
    oldGraphics.forEach(graphics => {
      graphics.mask = null
      this.stage.removeChild(graphics)
    })
    this.deferDestroy(oldGraphics)

    this.barsGraphicsBySubplot = this.subPlotMasks.map(mask => {
      const graphics = new PIXI.Graphics()
      graphics.eventMode = "none"
      graphics.mask = mask
      this.stage.addChild(graphics)
      return graphics
    })
    // sprites render normally until the next updateBarsLayer decides to hide them
    this.sprites.forEach(sprite => { sprite.renderable = true })
    this.barsLayerActive = false
  }

  /**
   * Re-apply masks to all sprites based on their subPlotNum stored in state.
   * Called after resize when new masks are created, after state has been updated.
   */
  private reapplyMasksFromState(): void {
    this.sprites.forEach((sprite, pointId) => {
      const pointState = this.state.getPoint(pointId)
      if (pointState?.subPlotNum !== undefined) {
        sprite.mask = this.subPlotMasks[pointState.subPlotNum] ?? null
      }
    })
  }

  protected doMatchPointsToData(
    datasetID: string,
    caseData: CaseDataWithSubPlot[],
    displayType: PointDisplayType,
    style: IPointStyle
  ): void {
    if (this.isDisposed) {
      console.warn("PixiPointRenderer.doMatchPointsToData: called after dispose, ignoring")
      return
    }
    if (!this.renderer) {
      console.warn("PixiPointRenderer.doMatchPointsToData: renderer not initialized, skipping")
      return
    }

    // Ensure sprites exist for any points in state that don't have sprites yet
    // This handles the case where syncFromState was called before state was populated
    if (this.state.size > 0 && this.sprites.size === 0) {
      this.syncFromState()
    }

    // Handle display type transition
    const oldDisplayType = this._displayType
    if (oldDisplayType !== displayType && this.sprites.size > 0 && this.sprites.size === caseData.length) {
      this.displayTypeTransitionState.isActive = true
      this.state.forEach(point => {
        this.pointTransitionStates.set(point.id, { hasTransitioned: false })
      })
    }
    // Set display type before the early return so texture/anchor logic sees the new type.
    // (The base class also sets this after we return, but we need it set for the code below.)
    this._displayType = displayType

    if (this.displayTypeTransitionState.isActive) {
      // Sync subplot assignment even when deferring the rest: a coincident subplot layout change
      // (e.g. adding a second categorical axis) would otherwise leave sprites masked to old cells.
      this.state.updateSubPlotNumsFromCaseData(caseData)
      this.applyMasks(caseData)
      this.doStartRendering()
      return
    }

    // Sync state with case data
    const { added, removed } = this.state.syncWithCaseData(caseData, style)

    // Remove sprites for removed points
    removed.forEach(pointId => {
      const sprite = this.sprites.get(pointId)
      if (sprite) {
        sprite.destroy()
        this.sprites.delete(pointId)
      }
    })

    // Clean up orphan sprites — sprites that exist in the sprites map but have no corresponding
    // entry in the shared PointsState. This can happen when the NullPointRenderer (which shares
    // the same PointsState) removes points from state while the PixiPointRenderer has sprites
    // for those points (e.g., due to the async timing between PIXI renderer initialization and
    // plugin-driven attribute updates). We don't guard on size because the NullPointRenderer
    // could replace N cases with N different cases, leaving sprites.size === state.size but
    // with mismatched IDs.
    const orphanIds: string[] = []
    this.sprites.forEach((_sprite, pointId) => {
      if (!this.state.getPoint(pointId)) {
        orphanIds.push(pointId)
      }
    })
    orphanIds.forEach(pointId => {
      const sprite = this.sprites.get(pointId)
      if (sprite) {
        sprite.destroy()
        this.sprites.delete(pointId)
      }
    })
    // Create sprites for any state points that don't have sprites yet (the counterpart to
    // orphan removal — the NullPointRenderer may have added new points to shared state).
    if (this.state.size > this.sprites.size) {
      this.syncFromState()
    }

    // Update anchor based on display type
    this._anchor = displayType === "points" ? circleAnchor :
                   displayType === "bars" ? hBarAnchor : circleAnchor

    const texture = this.getPointTexture(style)

    // Create sprites for added points (skip any already created by syncFromState above)
    added.forEach(pointId => {
      if (!this.sprites.has(pointId)) {
        const sprite = this.getNewSprite(pointId, texture, style)
        this.pointsContainer.addChild(sprite)
        this.sprites.set(pointId, sprite)
      }
    })

    // Update existing sprites
    this.sprites.forEach((sprite, pointId) => {
      if (!added.includes(pointId)) {
        if (sprite.texture !== texture) {
          sprite.texture = texture
        }
        // against the uniform style, which is the one that drew the texture just assigned -- a
        // point's own stored style can still be the one it had under the previous display type
        this.syncHitArea(sprite, style)
      }
    })

    // Apply masks
    this.applyMasks(caseData)

    // Reset scale
    this.doSetAllPointsScale(1, 0)

    this.doStartRendering()
  }

  protected doSetPointPosition(pointId: string, x: number, y: number): void {
    const sprite = this.sprites.get(pointId)
    if (sprite) {
      this.setPointXyProperty("position", sprite, x, y)
    }
  }

  protected doSetPointScale(pointId: string, scale: number): void {
    const sprite = this.sprites.get(pointId)
    if (sprite) {
      this.setPointXyProperty("scale", sprite, scale, scale)
    }
  }

  protected doSetPointStyle(pointId: string, style: Partial<IPointStyle>): void {
    const sprite = this.sprites.get(pointId)
    if (!sprite) return

    // If transitioning from bars to points, skip style update
    if (this._displayType === "points" && this.displayTypeTransitionState.isActive) return

    const pointState = this.state.getPoint(pointId)
    if (!pointState) return

    const newStyle = { ...pointState.style, ...style }
    const texture = this.getPointTexture(newStyle)
    if (sprite.texture !== texture) {
      sprite.texture = texture
    }
    this.syncHitArea(sprite, newStyle)

    this.doStartRendering()
  }

  protected doSetPointRaised(pointId: string, raised: boolean): void {
    const sprite = this.sprites.get(pointId)
    if (sprite) {
      sprite.zIndex = raised ? RAISED_Z_INDEX : DEFAULT_Z_INDEX
      this.doStartRendering()
    }
  }

  protected doSetPointSubPlot(pointId: string, subPlotIndex: number): void {
    const sprite = this.sprites.get(pointId)
    if (sprite) {
      sprite.mask = this.subPlotMasks[subPlotIndex]
      this.doStartRendering()
    }
  }

  protected async doTransition(callback: () => void, duration: number): Promise<void> {
    if (duration === 0) {
      callback()
      return
    }
    return new Promise<void>(resolve => {
      this.currentTransition = new PixiTransition(duration, () => resolve())
      callback()
      this.currentTransition = undefined
      this.doStartRendering()
    })
  }

  protected doStartRendering(): void {
    if (this.isDisposed) {
      console.warn("PixiPointRenderer.doStartRendering: called after dispose, ignoring")
      return
    }
    if (!this.ticker.started) {
      this.ticker.start()
    }
  }

  protected doRemoveMasks(): void {
    // Clear sprite and coalesced-bars-layer mask references before the masks are destroyed, so
    // nothing renders a destroyed mask (which throws on a null GPU context).
    this.sprites.forEach(sprite => {
      sprite.mask = null
    })
    this.barsGraphicsBySubplot.forEach(graphics => {
      graphics.mask = null
    })
    // Defer destruction of old masks (see deferDestroy)
    const oldMasks = this.subPlotMasks
    this.subPlotMasks = []
    this.deferDestroy(oldMasks)
    // Trigger a render to show sprites without masks
    this.doStartRendering()
  }

  protected doSetVisibility(isVisible: boolean): void {
    this.pointsContainer.visible = isVisible
    this.doStartRendering()
  }

  // One flag skips hit-testing of every point sprite (each has eventMode "static"); the background
  // stays interactive so the marquee's pointer passthrough is unaffected.
  override setPointsInteractive(interactive: boolean): void {
    this.pointsContainer.interactiveChildren = interactive
  }

  protected async doSetAllPointsScale(scale: number, duration: number): Promise<void> {
    return this.doTransition(() => {
      this.sprites.forEach(sprite => {
        this.setPointXyProperty("scale", sprite, scale, scale)
      })
    }, duration)
  }

  protected doSetPositionOrTransition(
    pointId: string,
    style: Partial<IPointStyle>,
    x: number,
    y: number
  ): void {
    const sprite = this.sprites.get(pointId)
    if (!sprite) return

    if (this.displayTypeTransitionState.isActive) {
      this.transitionPointDisplayType(pointId, sprite, style, x, y)
    } else {
      this.setPointXyProperty("anchor", sprite, this._anchor.x, this._anchor.y)
      this.setPointXyProperty("position", sprite, x, y)
      this.setPointXyProperty("scale", sprite, 1, 1)
    }
  }

  protected doSetupBackgroundEventDistribution(options: IBackgroundEventDistributionOptions): void {
    const { elementToHide } = options

    const getElementUnderCanvas = (event: PIXI.FederatedPointerEvent) => {
      const originalPointerEvents = elementToHide.style.pointerEvents
      elementToHide.style.pointerEvents = "none"
      const elementUnderneath = document.elementFromPoint(event.clientX, event.clientY)
      elementToHide.style.pointerEvents = originalPointerEvents
      return elementUnderneath
    }

    this.background.eventMode = "static"

    // Skip federated events whose native event we dispatched, to avoid infinite recursion
    // in Safari (CODAP-1367), where synthetic events re-enter PIXI's EventSystem.
    const isOwnDispatchedEvent = (event: PIXI.FederatedPointerEvent) =>
      !!event.nativeEvent && !!getRendererForEvent(event.nativeEvent as Event)

    this.background.on("click", (event: PIXI.FederatedPointerEvent) => {
      if (isOwnDispatchedEvent(event)) return
      this.dispatchEvent(getElementUnderCanvas(event), new MouseEvent("click", event), event)
    })

    this.background.on("pointerdown", (event: PIXI.FederatedPointerEvent) => {
      if (isOwnDispatchedEvent(event)) return
      this.dispatchEvent(getElementUnderCanvas(event), new PointerEvent("pointerdown", event), event)
    })

    let mouseoverElement: Element | null = null
    this.background.on("mousemove", (event: PIXI.FederatedPointerEvent) => {
      if (isOwnDispatchedEvent(event)) return
      const elementUnderneath = getElementUnderCanvas(event)

      // Dispatch mousemove on every move to enable cursor updates (e.g., for zoom mode)
      // even when the cursor stays over the same element.
      if (elementUnderneath) {
        this.dispatchEvent(elementUnderneath, new MouseEvent("mousemove", event), event)
      }

      // Handle mouseover/mouseout when element changes
      if (elementUnderneath && elementUnderneath === mouseoverElement) {
        return
      }
      if (elementUnderneath) {
        if (mouseoverElement && mouseoverElement !== elementUnderneath) {
          this.dispatchEvent(mouseoverElement, new MouseEvent("mouseout", event), event)
        }
        this.dispatchEvent(elementUnderneath, new MouseEvent("mouseover", event), event)
        mouseoverElement = elementUnderneath
      } else if (mouseoverElement) {
        this.dispatchEvent(mouseoverElement, new MouseEvent("mouseout", event), event)
        mouseoverElement = null
      }
    })

    this.background.on("mouseout", (event: PIXI.FederatedPointerEvent) => {
      if (isOwnDispatchedEvent(event)) return
      this.dispatchEvent(mouseoverElement, new MouseEvent("mouseout", event), event)
      mouseoverElement = null
    })
  }

  // ===== Private helper methods =====

  private tick(): void {
    if (this.isDisposed || !this.renderer) {
      this.ticker.stop()
      return
    }
    if (this.anyTransitionActive) {
      PixiTransition.transitionStep(this.targetProp, this.startProp)
    } else {
      this.ticker.stop()
      this.cleanupUnusedTextures()
    }
    this.updateBarsLayer()
    this.renderer.render(this.stage)
  }

  // Rebuilds the coalesced-bars layer from the current point state. When fully in bars mode, each
  // bar's contiguous same-style cases are merged into one solid segment rect (with its stroke), and
  // the per-case sprites are hidden from rendering (renderable = false). In points mode or during
  // the points<->bars transition, the layer is cleared and the sprites render normally. Bar clicks
  // are handled by the SVG bar covers, so hiding the sprites doesn't affect selection. See
  // CODAP-1234.
  private updateBarsLayer(): void {
    // Coalesce only fused bars and not mid-transition (see shouldCoalesceBars); otherwise keep
    // per-case sprite rendering so transitions animate and non-fused "bars" draw per case.
    const useCoalesced = this.shouldCoalesceBars
    if (!useCoalesced) {
      if (this.barsLayerActive) {
        this.barsGraphicsBySubplot.forEach(graphics => graphics.clear())
        this.sprites.forEach(sprite => { sprite.renderable = true })
        this.barsLayerActive = false
      }
      return
    }

    // Group pieces by subplot so each cell coalesces independently: bars in different split-plot
    // cells must not be merged even when they share a primary coordinate (and each cell's layer is
    // masked to its cell). Each subplot's runs are drawn into its own masked Graphics.
    const piecesBySubplot = new Map<number, IBarPiece[]>()
    this.state.forEach(pointState => {
      const piece = pointStateToBarPiece(pointState)
      if (!piece) return
      const subplot = pointState.subPlotNum ?? 0
      const pieces = piecesBySubplot.get(subplot) ?? []
      pieces.push(piece)
      piecesBySubplot.set(subplot, pieces)
    })

    this.barsGraphicsBySubplot.forEach((graphics, subplot) => {
      graphics.clear()
      const pieces = piecesBySubplot.get(subplot)
      if (!pieces) return
      for (const run of coalesceBars(pieces, this._anchor, this._barStackAxis)) {
        // chain fill/stroke onto the same rect() command, matching getRectTexture's usage
        if (run.strokeWidth > 0) {
          graphics.rect(run.left, run.top, run.width, run.height)
            .fill(run.fill)
            .stroke({ color: run.stroke, width: run.strokeWidth, alpha: run.strokeOpacity })
        } else {
          graphics.rect(run.left, run.top, run.width, run.height).fill(run.fill)
        }
      }
    })
    this.sprites.forEach(sprite => { sprite.renderable = false })
    this.barsLayerActive = true
  }

  private syncFromState(): void {
    // Create sprites for any points in state that don't have sprites yet
    if (!this.renderer) {
      console.warn("PixiPointRenderer.syncFromState: renderer not initialized, skipping")
      return
    }

    this.state.forEach(pointState => {
      if (!this.sprites.has(pointState.id)) {
        try {
          const texture = this.getPointTexture(pointState.style)
          const sprite = this.getNewSprite(pointState.id, texture, pointState.style)
          sprite.position.set(pointState.x, pointState.y)
          sprite.scale.set(pointState.scale)
          sprite.zIndex = pointState.isRaised ? RAISED_Z_INDEX : DEFAULT_Z_INDEX
          this.pointsContainer.addChild(sprite)
          this.sprites.set(pointState.id, sprite)
        } catch (error) {
          console.error("PixiPointRenderer.syncFromState: error creating sprite for", pointState.id, error)
        }
      }
    })
  }

  private getNewSprite(pointId: string, texture: PIXI.Texture, style: IPointStyle): PIXI.Sprite {
    const sprite = new PIXI.Sprite(texture)
    sprite.anchor.copyFrom(this._anchor)
    sprite.zIndex = DEFAULT_Z_INDEX
    this.syncHitArea(sprite, style)
    this.setupSpriteInteractivity(pointId, sprite)
    return sprite
  }

  /*
   * Bars keep the sprite's own rectangular test, which is what a bar is. Only a point carries a
   * shape to test against.
   */
  private syncHitArea(sprite: PIXI.Sprite, style: IPointStyle): void {
    if (this._displayType !== "points") {
      sprite.hitArea = null
      return
    }
    const shape = style.shape ?? kDefaultPointShape
    if (sprite.hitArea instanceof PointShapeHitArea) {
      sprite.hitArea.update(shape, style.radius)
    } else {
      sprite.hitArea = new PointShapeHitArea(shape, style.radius)
    }
  }

  private setPointXyProperty(prop: TransitionProp, sprite: PIXI.Sprite, x: number, y: number): void {
    if (this.currentTransition) {
      this.setTargetXyProp(prop, sprite, x, y)
    } else {
      const targetProp = this.targetProp[prop]
      if (targetProp?.has(sprite)) {
        targetProp.delete(sprite)
      }
      sprite[prop].set(x, y)
      this.doStartRendering()
    }
  }

  private setTargetXyProp(propKey: TransitionProp, sprite: PIXI.Sprite, x: number, y: number): void {
    if (!this.currentTransition) return

    let targetProp = this.targetProp[propKey]
    let startProp = this.startProp[propKey]
    if (!targetProp || !startProp) {
      targetProp = this.targetProp[propKey] = new Map()
      startProp = this.startProp[propKey] = new Map()
    }
    targetProp.set(sprite, { x, y, transition: this.currentTransition })
    startProp.set(sprite, { x: sprite[propKey].x, y: sprite[propKey].y, transition: this.currentTransition })
  }

  private textureKey(style: IPointStyle, includeDimensions = false): string {
    let keyStyle: Record<string, unknown> = { ...style, displayType: this._displayType }

    if (
      (!includeDimensions && (this._displayType === "bars" && this.displayTypeTransitionState.isActive)) ||
      (this._displayType === "points" && !this.displayTypeTransitionState.isActive)
    ) {
      const { width, height, ...rest } = keyStyle
      keyStyle = rest
    }
    return JSON.stringify(keyStyle)
  }

  private getPointTexture(style: IPointStyle, includeDimensions = false): PIXI.Texture {
    return this._displayType === "bars"
      ? this.getRectTexture(style, includeDimensions)
      : this.getShapeTexture(style)
  }

  private getShapeTexture(style: IPointStyle): PIXI.Texture {
    const { radius, fill, stroke, strokeWidth, strokeOpacity } = style
    const key = this.textureKey(style)

    if (this.textures.has(key)) {
      return this.textures.get(key) as PIXI.Texture
    }

    const shape = style.shape ?? kDefaultPointShape
    const geometry = pointShapeGeometry(shape, radius)
    const graphics = new PIXI.Graphics()
    if (geometry.kind === "circle") {
      graphics.circle(0, 0, geometry.radius)
    } else {
      graphics.poly(geometry.points.flatMap(({ x, y }) => [x, y]))
    }
    graphics
      .fill(fill)
      // Rounded joins are required of every surface (see point-shapes.ts), and doubly so here: a
      // miter at a star's tips also reaches past the stroke padding below and clips.
      .stroke({ color: stroke, width: strokeWidth, alpha: strokeOpacity ?? 0.4, join: "round" })

    // Circles size their own texture; everything else needs an explicit frame, for the reason
    // given on symmetricFrame.
    const frame = geometry.kind === "circle" ? undefined : symmetricFrame(shape, radius, strokeWidth)
    return this.generateTexture(graphics, key, frame)
  }

  private getRectTexture(style: IPointStyle, includeDimensions = false): PIXI.Texture {
    const { radius, fill, stroke, strokeWidth, strokeOpacity, width, height } = style
    const key = this.textureKey(style, includeDimensions)

    if (this.textures.has(key)) {
      return this.textures.get(key) as PIXI.Texture
    }

    const shouldDrawStroke = (dimension: number | undefined) => {
      return includeDimensions || !this.displayTypeTransitionState.isActive &&
        isFiniteNumber(dimension) && dimension >= 3
    }

    const textureStrokeWidth = shouldDrawStroke(width) || shouldDrawStroke(height) ? strokeWidth : 0

    const rectWidth = isFiniteNumber(width) && (!this.displayTypeTransitionState.isActive || includeDimensions)
      ? width : radius * 2
    const rectHeight = isFiniteNumber(height) && (!this.displayTypeTransitionState.isActive || includeDimensions)
      ? height : radius * 2

    const graphics = new PIXI.Graphics()
      .rect(0, 0, rectWidth, rectHeight)
      .fill(fill)
      .stroke({ color: stroke, width: textureStrokeWidth, alpha: strokeOpacity ?? 0.4 })

    return this.generateTexture(graphics, key)
  }

  private generateTexture(graphics: PIXI.Graphics, key: string, frame?: PIXI.Rectangle): PIXI.Texture {
    if (!this.renderer) {
      throw new Error("PixiPointRenderer renderer not initialized")
    }
    const texture = this.renderer.generateTexture({
      target: graphics,
      resolution: devicePixelRatio * MAX_SPRITE_SCALE,
      ...(frame ? { frame } : {})
    })

    this.textures.set(key, texture)
    return texture
  }

  private cleanupUnusedTextures(): void {
    const texturesInUse = new Set<PIXI.Texture>()
    this.sprites.forEach(sprite => {
      texturesInUse.add(sprite.texture)
    })
    for (const [key, texture] of this.textures) {
      if (!texturesInUse.has(texture)) {
        texture.destroy()
        this.textures.delete(key)
      }
    }
  }

  private dispatchEvent(targetElement: Element | null, event: Event, _pixiEvent: PIXI.FederatedEvent): void {
    if (targetElement) {
      this.registerDispatchedEvent(event)
      targetElement.dispatchEvent(event)
    }
  }

  private applyMasks(allCaseData: CaseDataWithSubPlot[]): void {
    if (!this.renderer || (window as any).Cypress) return

    allCaseData.forEach(caseData => {
      const pointId = this.state.getPointIdForCaseData(caseData)
      if (pointId) {
        const sprite = this.sprites.get(pointId)
        if (sprite) {
          const subPlotNum = caseData.subPlotNum
          sprite.mask = subPlotNum !== undefined ? (this.subPlotMasks[subPlotNum] ?? null) : null
        }
      }
    })
  }

  private async transitionPointDisplayType(
    pointId: string,
    sprite: PIXI.Sprite,
    style: Partial<IPointStyle>,
    x: number,
    y: number
  ): Promise<void> {
    const { width, height, radius = 6 } = style
    const isBar = this._displayType === "bars" && isFiniteNumber(width) && isFiniteNumber(height)
    const isPoint = this._displayType === "points" && isFiniteNumber(radius)

    if (!isBar && !isPoint) return

    /*
     * Points animate to the size of the texture that is about to replace the bar's, rather than to
     * 2r. The texture is the shape's symmetric extent plus its stroke -- about 2.7r wide for a star
     * -- and it arrives at scale 1, so animating to 2r would end the transition with the sprite
     * jumping to its real size.
     */
    const destPointState = isPoint ? this.state.getPoint(pointId) : undefined
    const destTexture = destPointState
      ? this.getPointTexture({ ...destPointState.style, ...style }, true)
      : undefined

    const newWidth = isBar ? width - 1 : destTexture?.width ?? radius * 2
    const newHeight = isBar ? height - 1 : destTexture?.height ?? radius * 2

    const scaleXFactor = newWidth / sprite.width
    const scaleYFactor = newHeight / sprite.height

    await this.doTransition(() => {
      this.setPointXyProperty("anchor", sprite, this._anchor.x, this._anchor.y)
      this.setPointXyProperty("scale", sprite, scaleXFactor, scaleYFactor)
      this.setPointXyProperty("position", sprite, x, y)
    }, transitionDuration)

    this.pointTransitionStates.set(pointId, { hasTransitioned: true })
    const pointState = this.state.getPoint(pointId)
    if (pointState) {
      const newStyle = { ...pointState.style, ...style }
      const texture = this.getPointTexture(newStyle, true)
      // the display type has changed by now, so what the sprite should be hit tested against has too
      this.syncHitArea(sprite, newStyle)

      if (sprite.texture !== texture) {
        sprite.texture = texture
        this.setPointXyProperty("anchor", sprite, this._anchor.x, this._anchor.y)
        this.setPointXyProperty("scale", sprite, 1, 1)
      }
    }

    const allTransitioned = Array.from(this.pointTransitionStates.values()).every(s => s.hasTransitioned)
    if (allTransitioned) {
      this.displayTypeTransitionState.isActive = false
    }
  }

  private dispatchForSafari(event: PIXI.FederatedPointerEvent, eventType: string): void {
    const x = event.clientX
    const y = event.clientY
    const canvas = this.renderer?.view.canvas
    if (canvas) {
      const canvasStyle = canvas.style as CSSStyleDeclaration
      canvasStyle.visibility = "hidden"
      const svgElement = (eventType === "mouseout") ? this.mostRecentSvgElement : document.elementFromPoint(x, y)
      canvasStyle.visibility = "visible"
      if (svgElement?.tagName === "rect") {
        const mouseEvent = new MouseEvent(eventType, {
          bubbles: true,
          cancelable: true,
          clientX: x,
          clientY: y,
        })
        svgElement.dispatchEvent(mouseEvent)
        this.mostRecentSvgElement = svgElement as SVGElement
      }
    }
  }

  private setupSpriteInteractivity(pointId: string, sprite: PIXI.Sprite): void {
    sprite.eventMode = "static"
    sprite.cursor = "pointer"

    let draggingActive = false

    const getPointAndMetadata = (): { point: IPoint, metadata: IPointMetadata } | null => {
      const pointState = this.state.getPoint(pointId)
      if (!pointState) return null
      return {
        point: { id: pointId },
        metadata: {
          caseID: pointState.caseID,
          plotNum: pointState.plotNum,
          datasetID: pointState.datasetID,
          style: { ...pointState.style },
          x: pointState.x,
          y: pointState.y
        }
      }
    }

    const handlePointerOver = (pointerEvent: PIXI.FederatedPointerEvent) => {
      const elementOnTop = document.elementFromPoint(pointerEvent.clientX, pointerEvent.clientY)
      const pointerState = PointerState.getInstance()
      if (elementOnTop !== this.canvas || pointerState.pointerIsDown()) {
        return
      }

      const data = getPointAndMetadata()
      if (!data) return

      if (this._displayType === "bars") {
        if (!this._pointsFusedIntoBars) {
          const newStyle = { ...data.metadata.style, stroke: strokeColorHover }
          this.doSetPointStyle(pointId, newStyle)
        } else if (this.isSafari) {
          this.dispatchForSafari(pointerEvent, "mouseover")
        }
      } else {
        this.doTransition(() => {
          this.setPointXyProperty("scale", sprite, hoverRadiusFactor, hoverRadiusFactor)
        }, transitionDuration)
      }

      if (!draggingActive) {
        !this._pointsFusedIntoBars && this.onPointerOver?.(toPointerEvent(pointerEvent), data.point, data.metadata)
      } else {
        this.onPointerLeave?.(toPointerEvent(pointerEvent), data.point, data.metadata)
      }
    }

    const handlePointerLeave = (pointerEvent: PIXI.FederatedPointerEvent) => {
      const data = getPointAndMetadata()
      if (!data) return

      if (this._displayType === "bars") {
        if (!this._pointsFusedIntoBars) {
          const newStyle = { ...data.metadata.style, stroke: strokeColor }
          this.doSetPointStyle(pointId, newStyle)
        } else if (this.isSafari) {
          this.dispatchForSafari(pointerEvent, "mouseout")
        }
      } else {
        this.doTransition(() => {
          this.setPointXyProperty("scale", sprite, 1, 1)
        }, transitionDuration)
      }

      !this._pointsFusedIntoBars && this.onPointerLeave?.(toPointerEvent(pointerEvent), data.point, data.metadata)
    }

    sprite.on("pointerover", (pointerEvent: PIXI.FederatedPointerEvent) => {
      handlePointerOver(pointerEvent)
    })

    sprite.on("pointerleave", (pointerEvent: PIXI.FederatedPointerEvent) => {
      if (!draggingActive) {
        handlePointerLeave(pointerEvent)
      }
    })

    sprite.on("click", (clickEvent: PIXI.FederatedPointerEvent) => {
      // Stop propagation to prevent the click from bubbling to the background,
      // which would trigger deselection of the point we just selected
      clickEvent.stopPropagation()

      const data = getPointAndMetadata()
      if (!data) return

      if (this._displayType === "bars" && this._pointsFusedIntoBars && this.isSafari) {
        this.dispatchForSafari(clickEvent, "click")
      } else {
        this.onPointerClick?.(toPointerEvent(clickEvent), data.point, data.metadata)
      }
    })

    sprite.on("pointerdown", (pointerDownEvent: PIXI.FederatedPointerEvent) => {
      // Stop propagation to prevent the pointerdown from bubbling to the background
      pointerDownEvent.stopPropagation()

      // Cancel any pending deselection. The window capture listener in useRendererPointerDown
      // fires before PIXI sees the event and schedules "deselectAll". We need to cancel it here
      // since we're clicking on a point, not the background.
      this.cancelAnimationFrame("deselectAll")

      const data = getPointAndMetadata()
      if (!data) return

      draggingActive = true
      this.onPointerDragStart?.(toPointerEvent(pointerDownEvent), data.point, data.metadata)

      const onDrag = (onDragEvent: PointerEvent) => {
        if (draggingActive && this._displayType !== "bars") {
          const currentData = getPointAndMetadata()
          currentData && this.onPointerDrag?.(onDragEvent, currentData.point, currentData.metadata)
        }
      }

      const onDragEnd = (pointerUpEvent: PointerEvent) => {
        if (draggingActive) {
          draggingActive = false
          const currentData = getPointAndMetadata()
          currentData && this.onPointerDragEnd?.(pointerUpEvent, currentData.point, currentData.metadata)
          handlePointerLeave(toFederatedPointerEvent(pointerUpEvent))
          window.removeEventListener("pointermove", onDrag)
          window.removeEventListener("pointerup", onDragEnd)
        }
      }

      window.addEventListener("pointermove", onDrag)
      window.addEventListener("pointerup", onDragEnd)
    })
  }

  // ===== Protected implementation of resize observer setup =====

  protected doSetupResizeObserver(resizeTo: HTMLElement): void {
    // Clean up existing observer if any
    this.resizeObserver?.disconnect()

    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        this.doResize(width, height, 1, 1, 1, 1)
      }
    })
    this.resizeObserver.observe(resizeTo)
  }
}

/**
 * Type guard to check if a renderer is a PixiPointRenderer (WebGL renderer)
 */
export function isPixiPointRenderer(renderer: PointRendererBase | undefined): renderer is PixiPointRenderer {
  return renderer instanceof PixiPointRenderer
}
